import { logger } from '@teko/log';
import {
  FCM_REGISTER_TOKEN_SUCCESS,
  FCM_TOKEN,
  FCM_TRANSPORT,
  FCM_USER_ID,
  NotificationData,
  Notification,
  UNS_APP_ID,
  TRACKING_ID_KEY,
  NotificationService,
  ExternalType,
} from '@teko/notification';
import type { Profile } from '@teko/user';
import type { MessagePayload } from 'firebase/messaging';
import { FirebaseApp, FirebaseOptions } from 'firebase/app';
import { DiscoveryService } from '@teko/discovery';
import { trackManually, trackVisibleManually } from '@teko/tracking';
import { NextRouter } from 'next/router';

export const getJson = (str: string) => {
  try {
    return !!str && JSON.parse(str);
  } catch (e) {
    return null;
  }
};

const getCookieByName = (cname: string) => {
  const match = document.cookie.match(new RegExp('(^| )' + cname + '=([^;]+)'));
  if (match) return match[2];
  return null;
};

export const registerUNS = async ({
  fcmToken,
  user,
  unsAppId,
  notificationService,
}: {
  fcmToken: string;
  user: Profile | null;
  unsAppId: string;
  notificationService: NotificationService;
}) => {
  const lsToken = localStorage.getItem(FCM_TOKEN);
  const lsUserId = localStorage.getItem(FCM_USER_ID);
  const lsUNSAppId = localStorage.getItem(UNS_APP_ID);
  const trackingId =
    getCookieByName(TRACKING_ID_KEY) || localStorage.getItem(TRACKING_ID_KEY);
  const lsRegisterTokenSuccess = localStorage.getItem(
    FCM_REGISTER_TOKEN_SUCCESS,
  );
  const registerCondition =
    // when firebase updates token, when first time users using the app
    fcmToken !== lsToken ||
    // when update notificationAppId config
    unsAppId !== lsUNSAppId ||
    // When the user logs in
    (user?.userId && user.userId !== lsUserId) ||
    // When registration failed last time
    lsRegisterTokenSuccess === 'false';

  if (registerCondition) {
    try {
      await notificationService.registerToken({
        app_id: unsAppId,
        user_id: user?.userId ?? undefined,
        transport: FCM_TRANSPORT,
        token: fcmToken,
        email: user?.email ?? undefined,
        phone: user?.telephone ?? undefined,
        userTrackingId: trackingId ?? undefined,
      });
      localStorage.setItem(FCM_REGISTER_TOKEN_SUCCESS, 'true');
      if (user?.userId) {
        localStorage.setItem(FCM_USER_ID, user.userId);
      }
    } catch (e) {
      localStorage.setItem(FCM_REGISTER_TOKEN_SUCCESS, 'false');
    }
  }
  localStorage.setItem(FCM_TOKEN, fcmToken);
  localStorage.setItem(UNS_APP_ID, unsAppId);
};

export const unregisterUNS = async (
  notificationService: NotificationService,
  unsAppId: string,
): Promise<void> => {
  try {
    const lsToken = localStorage.getItem(FCM_TOKEN);
    const lsUserId = localStorage.getItem(FCM_USER_ID);
    await notificationService.unRegisterToken({
      user_id: lsUserId ?? undefined,
      app_id: unsAppId,
      transport: FCM_TRANSPORT,
      token: lsToken ?? undefined,
    });
  } finally {
    localStorage.removeItem(FCM_USER_ID);
  }
};

interface NotificationConfigs {
  user: Profile | null;
  unsAppId: string;
  notificationService: NotificationService;
  firebaseOptions: FirebaseOptions;
  vapidKey: string;
  firebaseAppName?: string;
  externalScripts?: string[];
  onMessage?: (payload?: MessagePayload) => Promise<void>;
  markReadNotification?: (
    noti: Pick<Notification, 'id' | 'is_read'>,
  ) => Promise<void>;
}

const registerServiceWorker = async ({
  firebaseOptions,
  externalScripts,
  onMessage,
  markReadNotification,
}: Pick<
  NotificationConfigs,
  'firebaseOptions' | 'externalScripts' | 'onMessage' | 'markReadNotification'
>): Promise<ServiceWorkerRegistration> => {
  // Remove pushdy service worker
  try {
    const registrations = await navigator.serviceWorker.getRegistrations();
    const pushdyUrl = location.protocol + '//' + location.host + '/sw.js';
    registrations.forEach((reg) => {
      if (reg?.active?.scriptURL === pushdyUrl) {
        reg.unregister();
      }
    });
  } catch (e) {
    logger.error(e);
  }

  const configs = new URLSearchParams({
    ...firebaseOptions,
    externalScripts: (externalScripts || [])?.join(','),
  }).toString();

  const serviceWorker = await navigator.serviceWorker.register(
    `/firebase-messaging-sw.js?${configs}`,
  );
  // Listener for message comes from service worker
  navigator.serviceWorker.addEventListener('message', (event) => {
    const { action, msgId, traceId } = event.data;
    const payload = JSON.stringify({
      msg_id: msgId?.toString(),
      trace_id: traceId?.toString(),
    });
    switch (action) {
      case 'click':
        trackManually(
          'notification',
          'browserNotification',
          'notification',
          payload,
          msgId,
        );
        markReadNotification?.({ id: parseInt(msgId), is_read: false });
        break;
      case 'backgroundMessage':
        trackVisibleManually({
          regionName: 'browserNotification',
          contentName: 'notification',
          payload: payload,
          index: msgId,
        });
        onMessage?.();
        break;
      default:
        break;
    }
  });
  return serviceWorker;
};

const initFirebaseApp = async ({
  firebaseAppName,
  firebaseOptions,
}: Pick<
  NotificationConfigs,
  'firebaseOptions' | 'firebaseAppName'
>): Promise<FirebaseApp> => {
  const { getApp, initializeApp } = await import('firebase/app');

  let firebaseNotiApp;
  try {
    // try to get app by name
    // it will throw exception otherwise
    firebaseNotiApp = getApp(firebaseAppName);
  } catch (_) {
    // initialize if not exist yet
    firebaseNotiApp = initializeApp(firebaseOptions, firebaseAppName);
  }
  return firebaseNotiApp;
};

const registerFCM = async (
  firebaseApp: FirebaseApp,
  serviceWorker: ServiceWorkerRegistration | null,
  { vapidKey, onMessage }: Pick<NotificationConfigs, 'vapidKey' | 'onMessage'>,
) => {
  const {
    getMessaging,
    getToken,
    onMessage: addMessageEvent,
  } = await import('firebase/messaging');
  const messaging = getMessaging(firebaseApp);
  const token = await getToken(messaging, {
    vapidKey,
    serviceWorkerRegistration: serviceWorker ?? undefined,
  });

  if (onMessage) {
    addMessageEvent(messaging, onMessage);
  }

  return token;
};

export const installNotification = async ({
  user,
  unsAppId,
  notificationService,
  firebaseOptions,
  vapidKey,
  firebaseAppName = 'notification',
  externalScripts,
  onMessage,
  markReadNotification,
}: NotificationConfigs) => {
  try {
    if (
      'serviceWorker' in navigator &&
      typeof window.Notification !== 'undefined'
    ) {
      const permission = await window.Notification.requestPermission();
      if (permission === 'granted') {
        // Step 1: register new Service Worker to handle message in background
        const serviceWorker = await registerServiceWorker({
          firebaseOptions,
          externalScripts,
          onMessage,
          markReadNotification,
        });

        // Step 2: Init firebase app
        const firebaseApp = await initFirebaseApp({
          firebaseOptions,
          firebaseAppName,
        });
        // Step 3: register firebase cloud messaging
        const fcmToken = await registerFCM(firebaseApp, serviceWorker, {
          vapidKey,
          onMessage,
        });
        // Step 4: Register FCM token to UNS
        registerUNS({
          fcmToken,
          user,
          unsAppId,
          notificationService,
        });
      }
    }
  } catch (e) {
    logger.error(e);
  }
};

export const convertNotification = async (
  notification: NotificationData,
  discoveryService: DiscoveryService,
) => {
  if (!notification) return;
  const {
    notifications: data,
    check_point: checkpoint,
    unread: unRead,
  } = notification;
  let apiResultNotifications = data;
  if (!!apiResultNotifications.length) {
    // call API discovery to get image of order notification
    // array of order notification's order items
    const notificationOrderItemsArr = apiResultNotifications
      .filter((noti) => !!noti.order)
      .map((noti) =>
        typeof noti.order === 'string'
          ? getJson(noti.order)?.orderItems
          : noti.order?.orderItems || '',
      );
    if (notificationOrderItemsArr.length) {
      const products = await discoveryService.getProductDetailBySkus({
        skus: notificationOrderItemsArr,
      });
      const imageMapping: { [k: string]: string } = products.reduce(
        (prev: { [k: string]: string }, curr) => ({
          ...prev,
          [curr.productInfo?.sku]: curr.productInfo?.imageUrl,
        }),
        {},
      );
      apiResultNotifications = apiResultNotifications.map((noti) => {
        const orderItems =
          typeof noti.order === 'string'
            ? getJson(noti.order)?.orderItems
            : noti.order?.orderItems || '';
        return {
          ...noti,
          ...(!noti.order
            ? {}
            : {
                order: {
                  ...(typeof noti.order === 'string'
                    ? getJson(noti.order)
                    : noti.order),
                  imageUrl: imageMapping[orderItems] || '',
                },
              }),
        };
      });
    }
  }
  return {
    notifications: apiResultNotifications,
    checkpoint,
    unRead,
  };
};

export const onNotificationClick = async (
  notification: Notification,
  router: NextRouter,
) => {
  if (notification.deepLink) {
    router.push(notification.deepLink);
  } else if (notification.order) {
    router.push(
      {
        pathname: `/account/orders/[orderId]`,
        query: {
          orderId: notification.order.orderId,
        },
      },
      {
        pathname: `/account/orders/${notification.order.orderId}`,
      },
    );
  }
};

export const getExternalTypesByPath = (
  query: NextRouter['query'],
  tabNotification?: number,
): ExternalType[] => {
  if (
    query?.type === 'orders' ||
    // NOTIFICATION_TAB.ORDER_TAB === 1
    (!!tabNotification && tabNotification === 1)
  ) {
    return ['order'];
  }
  return ['noti', 'debt'];
};
